import React from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { GripVertical } from 'lucide-react';
import {
  LINK_IN_BIO_LIMITS,
  LINK_IN_BIO_SOCIAL_FORMATS,
  LINK_IN_BIO_SOCIAL_PLATFORMS,
  type LinkInBio,
  type LinkInBioSocialPlatform,
} from '@repo/lib';
import { getEnglishLinkInBioLabels } from '@repo/checkout';
import { cn } from '@dashboard/lib/utils';
import { Button } from '@dashboard/components/ui/button';
import { Input } from '@dashboard/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Reorderable from '@dashboard/components/Reorderable';
import SectionCard from './SectionCard';

const PLACEHOLDERS: Record<LinkInBioSocialPlatform, string> = {
  instagram: 'yourname',
  facebook: 'yourpage',
  tiktok: 'yourname',
  youtube: 'yourchannel',
  x: 'yourname',
  linkedin: 'company/your-name',
  whatsapp: '447700900000',
  email: 'hello@example.com',
  website: 'example.com',
};

const INPUT_TYPES: Partial<Record<LinkInBioSocialPlatform, string>> = {
  email: 'email',
  whatsapp: 'tel',
};

const platformLabels = getEnglishLinkInBioLabels().social;

const prefixFor = (platform: LinkInBioSocialPlatform) => {
  const format = LINK_IN_BIO_SOCIAL_FORMATS[platform];
  return 'prefix' in format ? format.prefix : null;
};

function SocialInput({
  index,
  platform,
  value,
  error,
}: {
  index: number;
  platform: LinkInBioSocialPlatform;
  value: string;
  error?: string;
}) {
  const { setFieldValue, setFieldTouched } = useFormikContext<LinkInBio>();
  const prefix = prefixFor(platform);
  const id = `social-${index}`;

  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div
        className={cn(
          'flex h-8 w-full overflow-hidden rounded-lg border border-input transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50',
          error && 'border-destructive',
        )}
      >
        {prefix && (
          <span className="flex shrink-0 items-center border-r border-input bg-muted px-2.5 text-sm text-muted-foreground">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          type={INPUT_TYPES[platform] ?? 'text'}
          aria-label={`${platformLabels[platform]} ${prefix ? 'username or link' : 'address'}`}
          aria-invalid={!!error}
          value={value}
          placeholder={PLACEHOLDERS[platform]}
          autoComplete="off"
          className="h-full min-w-0 rounded-none border-0 focus-visible:border-transparent focus-visible:ring-0 aria-invalid:ring-0"
          onChange={event => setFieldValue(`socials.${index}.url`, event.target.value)}
          onBlur={() => setFieldTouched(`socials.${index}.url`, true)}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function SocialsSection() {
  const { values, errors, touched, setFieldValue } = useFormikContext<LinkInBio>();
  const socials = values.socials ?? [];

  return (
    <FieldArray name="socials">
      {({ push, remove }) => (
        <SectionCard
          title="Social links"
          description="Shown as icons under your bio. Type your username, or paste a link."
          actions={
            <Button
              type="button"
              size="sm"
              disabled={socials.length >= LINK_IN_BIO_LIMITS.socials}
              onClick={() => push({ platform: 'instagram', url: '' })}
            >
              <PlusIcon />
              Add social link
            </Button>
          }
        >
          {socials.length === 0 ? (
            <p className="text-sm text-muted-foreground">No social links yet.</p>
          ) : (
            <Reorderable
              id="link-in-bio-socials"
              className="divide-y rounded-lg border px-3"
              items={socials.map((social, index) => ({ key: `social-${index}`, social }))}
              onReorder={(reordered: Array<{ social: (typeof socials)[number] }>) =>
                setFieldValue(
                  'socials',
                  reordered.map(item => item.social),
                )
              }
              renderItem={({
                item: { social },
                index,
                dragHandleProps,
              }: {
                item: { social: (typeof socials)[number] };
                index: number;
                dragHandleProps: React.HTMLAttributes<HTMLDivElement>;
              }) => (
                <div className="flex items-start gap-3 py-2.5">
                  <div
                    {...dragHandleProps}
                    aria-label={`Drag to reorder ${platformLabels[social.platform]}`}
                    className="mt-0.5 -ml-1 flex size-7 shrink-0 cursor-grab items-center justify-center rounded-md text-muted-foreground hover:bg-accent active:cursor-grabbing"
                  >
                    <GripVertical className="size-4" />
                  </div>
                  <Select
                    value={social.platform}
                    onValueChange={value => setFieldValue(`socials.${index}.platform`, value)}
                  >
                    <SelectTrigger className="w-32 shrink-0" aria-label="Platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_IN_BIO_SOCIAL_PLATFORMS.map(platform => (
                        <SelectItem key={platform} value={platform}>
                          {platformLabels[platform]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <SocialInput
                    index={index}
                    platform={social.platform}
                    value={social.url}
                    error={
                      getIn(touched, `socials.${index}.url`)
                        ? getIn(errors, `socials.${index}.url`)
                        : undefined
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="mt-0.5"
                    aria-label={`Remove ${platformLabels[social.platform]}`}
                    onClick={() => remove(index)}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              )}
            />
          )}
        </SectionCard>
      )}
    </FieldArray>
  );
}
